import { UPDATE_ROUTER, UPDATE_PRIVATE_BOARD } from './actions.js';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils';

/**
 * Parses AI chat URL parameters and returns structured state.
 *
 * @param {String} aichatParam - The aichat or card-aichat URL param value
 * @param {String|Boolean} showChatHistory - The show-chat-history URL param value
 * @param {Object} context - The context object { type, boardId, cardId? }
 * @returns {Object} { isOpened, chatId, showingHistory, isNewChat, context }
 */
const parseAiChatParam = (aichatParam, showChatHistory, context) => {
  if (!aichatParam) {
    return { isOpened: false, chatId: null, showingHistory: false, isNewChat: false, context: null };
  }

  const showingHistory = showChatHistory === 'true' || showChatHistory === true;

  // aichat=new means open new chat session (welcome screen)
  if (aichatParam === 'new') {
    return { isOpened: true, chatId: null, showingHistory, isNewChat: true, context };
  }

  // aichat=aic_xxx means open with specific chat
  return { isOpened: true, chatId: aichatParam, showingHistory, isNewChat: false, context };
};

/**
 * Derives page-level AI chat context from router state.
 *
 * @param {String} pageName - The page name (BOARD, HOME, SIGNUP, etc.)
 * @param {Object} pageParams - The page params
 * @returns {Object|null} Context object { type, boardId? } or null
 */
const derivePageAiChatContext = (pageName, pageParams) => {
  if (pageName === 'BOARD') {
    const boardId = pageParams?.boardId;
    return boardId ? { type: 'BOARD', boardId } : null;
  }
  if (pageName === 'BOARD_EXPLORER') {
    return { type: 'HOME', boardId: null };
  }
  if (pageName === 'SIGNUP') {
    return { type: 'ONBOARDING', boardId: null };
  }
  return null;
};

/**
 * Derives dialog-level AI chat context from router state.
 *
 * @param {String} dialogName - The dialog name (CARD, etc.)
 * @param {Object} dialogParams - The dialog params
 * @returns {Object|null} Context object { type, boardId, cardId } or null
 */
const deriveDialogAiChatContext = (dialogName, dialogParams) => {
  if (dialogName === 'CARD') {
    const boardId = dialogParams?.boardId;
    const cardId = dialogParams?.cardId;
    return boardId && cardId ? { type: 'CARD', boardId, cardId } : null;
  }
  return null;
};

/**
 * Page reducer
 * Handle `UPDATE_ROUTER` action to update `router` data on redux store.
 */
let initialState = {
  page: {
    name: 'LOADING',
    params: {},
    aichat: { isOpened: false, chatId: null, showingHistory: false, isNewChat: false, context: null }
  },
  dialog: {
    name: '',
    params: {},
    aichat: { isOpened: false, chatId: null, showingHistory: false, isNewChat: false, context: null }
  },
  accountId: 0
}
export const routerReducer = (state = initialState, action) => {
  switch (action.type) {
    case UPDATE_ROUTER:
      // Parse AI chat params from URL
      const pageAichatParam = action.router.page.params?.aichat;
      const dialogAichatParam = action.router.dialog.params?.['card-aichat'];
      const showChatHistoryParam = action.router.page.params?.['show-chat-history'];

      // Derive contexts
      const pageContext = derivePageAiChatContext(action.router.page.name, action.router.page.params);
      const dialogContext = deriveDialogAiChatContext(action.router.dialog.name, action.router.dialog.params);

      return {
        ...state,
        accountId: action.router.accountId,
        page: {
          ...state.page,
          name: action.router.page.name,
          params: {
            ...action.router.page.params,
            aichat: undefined,
            'show-chat-history': undefined
          },
          aichat: parseAiChatParam(pageAichatParam, showChatHistoryParam, pageContext)
        },
        dialog: {
          ...state.dialog,
          name: action.router.dialog.name,
          params: {
            ...action.router.dialog.params,
            aichat: undefined,
            'show-chat-history': undefined
          },
          aichat: parseAiChatParam(dialogAichatParam, showChatHistoryParam, dialogContext)
        }
      }
    case UPDATE_PRIVATE_BOARD:
      return ReduxUtils.replace(state, `page.params.private`, action.value);
    default:
      return state;
  }
};